export function SellVacantApartmentComplex() {
  return (
    <section className="py-20 bg-white" id="sell-vacant">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell Vacant Apartment Complex - Maximize Your Options
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A vacant apartment building presents unique opportunities and challenges. Learn how to position your empty 
            complex for the best possible sale outcome and attract the right type of buyers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Advantages of Selling Vacant Properties</h3>
            <p className="text-gray-600 mb-8">
              While vacant properties may seem like a disadvantage, they actually offer several benefits that can attract 
              specific types of investors and potentially increase your selling price.
            </p>
            
            <div className="space-y-6">
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                <h4 className="text-lg font-semibold text-green-800 mb-3">Complete Renovation Freedom</h4>
                <p className="text-gray-700">
                  Buyers can immediately begin renovations, upgrades, and modernization without tenant displacement issues 
                  or lease restrictions limiting their renovation plans.
                </p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">Flexible Showing Schedule</h4>
                <p className="text-gray-700">
                  Property viewings can be scheduled anytime without tenant notifications, allowing for more thorough 
                  inspections and multiple buyer visits.
                </p>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h4 className="text-lg font-semibold text-purple-800 mb-3">Market Rate Potential</h4>
                <p className="text-gray-700">
                  New owners can set current market rental rates without being locked into existing below-market leases 
                  or rent control restrictions.
                </p>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500">
                <h4 className="text-lg font-semibold text-orange-800 mb-3">Faster Closing Process</h4>
                <p className="text-gray-700">
                  No tenant lease transfers, security deposit handling, or tenant notification requirements streamline 
                  the closing process significantly.
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/1ed541883aa9356862d0737e2dd6e80b.jpg"
              alt="Vacant apartment complex ready for renovation"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Types of Buyers for Vacant Properties
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hammer-line text-3xl text-blue-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Fix & Flip Investors</h4>
              <p className="text-gray-600 mb-4">
                Investors who specialize in renovating properties quickly and reselling them at higher values within 
                6-18 months.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-800">Typical Offer: 70-80% of ARV</p>
                <p className="text-sm text-gray-600 mt-1">Fast closing, cash offers</p>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-green-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Buy & Hold Investors</h4>
              <p className="text-gray-600 mb-4">
                Long-term investors who plan to renovate and rent out units for ongoing passive income and 
                appreciation over time.
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-green-800">Typical Offer: 75-85% of Market Value</p>
                <p className="text-sm text-gray-600 mt-1">May use financing, longer timeline</p>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-community-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Developers</h4>
              <p className="text-gray-600 mb-4">
                Real estate developers who may renovate extensively or potentially demolish and rebuild depending 
                on zoning and market conditions.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-purple-800">Typical Offer: Varies widely</p>
                <p className="text-sm text-gray-600 mt-1">Based on development potential</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Preparing Your Vacant Property for Sale</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Security and Maintenance</h4>
                  <p className="text-gray-600">
                    Secure all entry points, maintain basic utilities, and perform regular property checks to prevent 
                    vandalism and deterioration.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Document Renovation Potential</h4>
                  <p className="text-gray-600">
                    Create detailed floor plans, highlight upgrade opportunities, and provide renovation cost estimates 
                    to help buyers visualize potential.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Market Analysis</h4>
                  <p className="text-gray-600">
                    Research comparable renovated properties and current rental rates to demonstrate income potential 
                    after improvements.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">4</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Professional Photography</h4>
                  <p className="text-gray-600">
                    Invest in high-quality photos that showcase the property's bones, natural light, and potential 
                    rather than focusing on current condition.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=Property%20inspector%20with%20clipboard%20and%20tablet%20documenting%20vacant%20apartment%20building%20condition%20and%20renovation%20potential%20professional%20assessment%20clean%20interior%20space&width=800&height=600&seq=sell-apt-inspect-6&orientation=landscape"
              alt="Property inspection of vacant apartment building"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-12 mb-16">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pro Tip: Highlight Future Income Potential</h3>
              <p className="text-gray-700 mb-6">
                Create a detailed proforma showing projected rental income after renovations. Include market rent comparables, 
                estimated renovation costs, and projected ROI to help investors understand the property's true potential value.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">Current Condition Value</h4>
                  <p className="text-2xl font-bold text-red-600">$X</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">Renovation Investment</h4>
                  <p className="text-2xl font-bold text-orange-600">+ $Y</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">After Renovation Value</h4>
                  <p className="text-2xl font-bold text-green-600">= $Z</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Turn Your Vacant Property Into Cash
          </h3>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            Don't let your vacant apartment building drain your finances. We specialize in purchasing empty properties 
            and can close quickly without the need for costly preparations or repairs.
          </p>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">No Prep</div>
              <div className="text-sm">Required</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">Fast</div>
              <div className="text-sm">Closing</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">Fair</div>
              <div className="text-sm">Cash Offer</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">No</div>
              <div className="text-sm">Commissions</div>
            </div>
          </div>
          <button className="bg-white text-purple-600 px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
            Get Cash Offer for Vacant Property
          </button>
        </div>
      </div>
    </section>
  );
}